import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as api from '@/features/backlog/api';
import { Attachment } from '@/features/backlog/types';
import { attachmentKind, isPreviewable, AttachmentKind } from '@/features/backlog/attachments';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';

// `itemPath` (e.g. "/spark-product-backlog/SPB-379") is the owning item's canonical path — passed so
// the lightbox can offer a shareable deep link to the current attachment (…/SPB-379?att=<id>).
type OpenFn = (attachments: Attachment[], startId: string, itemPath?: string) => void;
const Ctx = createContext<OpenFn | null>(null);

/** Open the attachment lightbox (or null off-provider). */
export const useLightbox = (): OpenFn | null => useContext(Ctx);

/** App-wide attachment lightbox: click an image/video attachment (or a description image) to view it
 *  full-screen with zoom/pan/pinch/scroll, a video player for videos, and a thumbnail strip to page
 *  through all of the task's attachments. Mount once (app.tsx). */
export function LightboxProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ items: Attachment[]; index: number; itemPath?: string } | null>(null);
  const open = useCallback<OpenFn>((attachments, startId, itemPath) => {
    const items = attachments.filter(isPreviewable);
    const index = Math.max(0, items.findIndex((a) => a.id === startId));
    if (items.length) setState({ items, index, itemPath });
  }, []);
  return (
    <Ctx.Provider value={open}>
      {children}
      {state ? (
        <Lightbox
          items={state.items}
          index={state.index}
          itemPath={state.itemPath}
          onIndex={(i) => setState((s) => (s ? { ...s, index: (i + s.items.length) % s.items.length } : s))}
          onClose={() => setState(null)}
        />
      ) : null}
    </Ctx.Provider>
  );
}

function Lightbox({ items, index, itemPath, onIndex, onClose }: { items: Attachment[]; index: number; itemPath?: string; onIndex: (i: number) => void; onClose: () => void }) {
  const current = items[index];
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [index]); // reset the "copied" tick when paging to another attachment
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex(index + 1);
      else if (e.key === 'ArrowLeft') onIndex(index - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, onIndex, onClose]);

  const kind = attachmentKind(current);

  // The bytes are auth'd (a bare href 401s), so Download / Open both fetch the blob first. Open pops
  // the tab SYNCHRONOUSLY in the click (or the browser blocks it) and navigates it once bytes arrive.
  const download = () => {
    api
      .fetchAttachmentBlob(current.id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = current.filename || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => undefined);
  };
  const openInNewTab = () => {
    const w = window.open('', '_blank');
    if (w) {
      try {
        w.opener = null; // sever the opener so an opened HTML attachment can't reach back into the app
      } catch {
        /* ignore */
      }
    }
    api
      .fetchAttachmentBlob(current.id)
      .then((blob) => {
        // Re-type so HTML/etc. RENDERS in the tab (not downloads) even if the blob came back generic.
        const typed = current.content_type && blob.type !== current.content_type ? new Blob([blob], { type: current.content_type }) : blob;
        const url = URL.createObjectURL(typed);
        if (w) w.location.href = url;
        else window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => w?.close());
  };
  // A shareable deep link to THIS attachment: the item's page URL + ?att=<id>. Opening it loads the
  // item and pops the lightbox on this attachment (see the drawer's `openAttachment` effect).
  const copyLink = () => {
    if (!itemPath) return;
    const link = `${window.location.origin}${itemPath}?att=${current.id}`;
    navigator.clipboard
      ?.writeText(link)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return createPortal(
    // Click anywhere on the backdrop to close; the content + controls below stop propagation so
    // clicking the image/video/demo or a control doesn't. (The HTML iframe swallows its own clicks.)
    <div className="fixed inset-0 z-[3000] flex flex-col bg-black/85 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={onClose}>
      {/* top bar */}
      <div className="flex items-center gap-3 px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <span className="min-w-0 truncate text-[13px] font-medium">{current.filename}</span>
        <span className="tnum text-[12px] text-white/50">
          {index + 1} / {items.length}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={download} aria-label="Download" title="Download" className="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
          </button>
          {itemPath ? (
            <button
              type="button"
              onClick={copyLink}
              aria-label="Copy link to this attachment"
              title={copied ? 'Link copied!' : 'Copy link to this attachment'}
              className={cn('flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/10', copied ? 'text-emerald-300' : 'text-white/80 hover:text-white')}
            >
              {copied ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 6.5" /></svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
              )}
            </button>
          ) : null}
          <button type="button" onClick={openInNewTab} aria-label="Open in new tab" title="Open in new tab" className="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4h6v6M20 4l-9 9M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" /></svg>
          </button>
          <span className="mx-1 h-5 w-px bg-white/15" />
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      </div>

      {/* stage — clicks on its empty area bubble to the backdrop and close; the media inside stops it */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {items.length > 1 ? (
          <>
            <NavArrow dir="left" onClick={() => onIndex(index - 1)} />
            <NavArrow dir="right" onClick={() => onIndex(index + 1)} />
          </>
        ) : null}
        {kind === 'video' ? (
          <VideoStage key={current.id} att={current} />
        ) : kind === 'html' ? (
          <HtmlStage key={current.id} att={current} />
        ) : kind === 'pdf' ? (
          <PdfStage key={current.id} att={current} />
        ) : kind === 'docx' ? (
          <DocxStage key={current.id} att={current} />
        ) : kind === 'xlsx' ? (
          <XlsxStage key={current.id} att={current} />
        ) : kind === 'pptx' ? (
          <PptxStage key={current.id} att={current} />
        ) : (
          <ImageStage key={current.id} att={current} />
        )}
      </div>

      {/* thumbnail strip */}
      {items.length > 1 ? (
        <div className="thin-scroll flex items-start gap-2 overflow-x-auto px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {items.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onIndex(i)}
              title={a.filename}
              className={cn('flex w-14 shrink-0 flex-col items-center gap-1 transition-opacity', i === index ? 'opacity-100' : 'opacity-60 hover:opacity-100')}
              aria-label={`View ${a.filename}`}
            >
              <span className={cn('relative h-14 w-14 overflow-hidden rounded-lg ring-2 transition-all', i === index ? 'ring-white' : 'ring-transparent')}>
                <Thumb att={a} />
              </span>
              <span className={cn('w-full truncate text-center text-[10px] leading-tight', i === index ? 'text-white/90' : 'text-white/50')}>{a.filename}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

function NavArrow({ dir, onClick }: { dir: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={dir === 'left' ? 'Previous' : 'Next'}
      className={cn('absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white/90 backdrop-blur transition-colors hover:bg-white/20', dir === 'left' ? 'left-3' : 'right-3')}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
      </svg>
    </button>
  );
}

/** Fetch an attachment blob (authed) into an object URL. Pass `mime` to force the blob's type (e.g. a
 *  PDF stored as octet-stream must be re-typed so the <iframe> renders it instead of downloading). */
function useBlobUrl(id: string, mime?: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let obj: string | null = null;
    setUrl(null);
    if (!id) return undefined; // skip (e.g. an HTML thumbnail, which uses an icon instead)
    api
      .fetchAttachmentBlob(id)
      .then((b) => {
        if (cancelled) return;
        const blob = mime && b.type !== mime ? new Blob([b], { type: mime }) : b;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [id, mime]);
  return url;
}

/** A compact coloured badge for a document type, used as a thumbnail + the doc-error/placeholder glyph. */
const DOC_BADGE: Partial<Record<AttachmentKind, { label: string; className: string }>> = {
  pdf: { label: 'PDF', className: 'bg-[#e2483d]' },
  docx: { label: 'DOC', className: 'bg-[#2b579a]' },
  xlsx: { label: 'XLS', className: 'bg-[#217346]' },
  pptx: { label: 'PPT', className: 'bg-[#c43e1c]' },
};
function DocBadge({ kind, className }: { kind: AttachmentKind; className?: string }) {
  const b = DOC_BADGE[kind];
  if (!b) return <FileGlyph className={className} />;
  return <span className={cn('flex items-center justify-center rounded font-bold text-white', b.className, className)}>{b.label}</span>;
}
function FileGlyph({ className }: { className?: string }) {
  return (
    <span className={cn('flex items-center justify-center text-white/60', className)}>
      <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v5h5M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" /></svg>
    </span>
  );
}
/** Shown when a doc fails to render inline (corrupt/unsupported) — offer the toolbar's Download instead. */
function DocError({ label = 'This file could not be previewed.' }: { label?: string }) {
  return <div className="max-w-[360px] px-6 text-center text-[13px] text-white/60" onClick={(e) => e.stopPropagation()}>{label}<br />Use Download above to open it.</div>;
}

function Thumb({ att }: { att: Attachment }) {
  const kind = attachmentKind(att);
  // Only images/videos get a rendered thumbnail; everything else shows a glyph (no fetch).
  const url = useBlobUrl(kind === 'image' || kind === 'video' ? att.id : '');
  if (kind === 'html')
    return (
      <span className="flex h-full w-full items-center justify-center bg-white/5 text-white/60">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /></svg>
      </span>
    );
  if (kind === 'pdf' || kind === 'docx' || kind === 'xlsx' || kind === 'pptx')
    return <span className="flex h-full w-full items-center justify-center bg-white/5 p-2.5"><DocBadge kind={kind} className="h-full w-full text-[10px]" /></span>;
  if (!url) return <span className="flex h-full w-full items-center justify-center bg-white/5"><Spinner className="h-3.5 w-3.5 text-white/60" /></span>;
  if (kind === 'video') return <video src={url} className="h-full w-full object-cover" muted />;
  return <img src={url} alt="" className="h-full w-full object-cover" />;
}

/** PDF: browser-native render in an <iframe> (bytes re-typed to application/pdf so it never downloads). */
function PdfStage({ att }: { att: Attachment }) {
  const url = useBlobUrl(att.id, 'application/pdf');
  if (!url) return <Spinner className="h-6 w-6 text-white/70" />;
  return (
    <div className="flex h-full w-full items-center justify-center p-2 sm:p-4" onClick={(e) => e.stopPropagation()}>
      <iframe src={url} title={att.filename} className="h-full w-full max-w-[1200px] rounded-lg bg-white shadow-2xl" />
    </div>
  );
}

/** Word (.docx): rendered client-side to HTML by docx-preview (lazy-loaded). */
function DocxStage({ att }: { att: Attachment }) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const [{ renderAsync }, blob] = await Promise.all([import('docx-preview'), api.fetchAttachmentBlob(att.id)]);
        if (cancelled || !ref.current) return;
        ref.current.innerHTML = '';
        await renderAsync(blob, ref.current, undefined, { className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true });
        if (!cancelled) setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [att.id]);
  return (
    <div className="thin-scroll flex h-full w-full items-start justify-center overflow-auto p-3 sm:p-6" onClick={(e) => e.stopPropagation()}>
      {state === 'loading' ? <div className="flex h-full items-center"><Spinner className="h-6 w-6 text-white/70" /></div> : null}
      {state === 'error' ? <div className="flex h-full items-center"><DocError /></div> : null}
      <div ref={ref} className={cn('docx-host w-full max-w-[900px]', state !== 'ready' && 'hidden')} />
    </div>
  );
}

/** Excel (.xlsx): parsed client-side by SheetJS (lazy-loaded), each sheet rendered as an HTML table. */
function XlsxStage({ att }: { att: Attachment }) {
  const [sheets, setSheets] = useState<{ name: string; html: string }[] | null>(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError(false);
    setActive(0);
    (async () => {
      try {
        const [XLSX, blob] = await Promise.all([import('xlsx'), api.fetchAttachmentBlob(att.id)]);
        const buf = await blob.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const out = wb.SheetNames.map((name) => ({ name, html: XLSX.utils.sheet_to_html(wb.Sheets[name]) }));
        if (!cancelled) setSheets(out.length ? out : null);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [att.id]);
  if (error) return <DocError />;
  if (!sheets) return <Spinner className="h-6 w-6 text-white/70" />;
  return (
    <div className="flex h-full w-full flex-col p-3 sm:p-6" onClick={(e) => e.stopPropagation()}>
      {sheets.length > 1 ? (
        <div className="thin-scroll mb-2 flex shrink-0 gap-1 overflow-x-auto pb-1">
          {sheets.map((s, i) => (
            <button
              key={s.name}
              type="button"
              onClick={() => setActive(i)}
              className={cn('shrink-0 rounded-md px-3 py-1 text-[12px] font-medium transition-colors', i === active ? 'bg-white text-neutral-900' : 'bg-white/10 text-white/70 hover:bg-white/20')}
            >
              {s.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="xlsx-host thin-scroll min-h-0 w-full max-w-[1200px] flex-1 self-center overflow-auto rounded-lg bg-white p-2 text-[12px] text-neutral-900" dangerouslySetInnerHTML={{ __html: sheets[active].html }} />
    </div>
  );
}

/** PowerPoint (.pptx): no good client-side renderer — show the file + point at Download / Open. */
function PptxStage({ att }: { att: Attachment }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3" onClick={(e) => e.stopPropagation()}>
      <DocBadge kind="pptx" className="h-16 w-16 text-[14px]" />
      <div className="text-[14px] font-medium text-white/85">{att.filename}</div>
      <div className="max-w-[380px] text-center text-[12.5px] leading-relaxed text-white/55">
        Inline preview isn’t available for PowerPoint files. Use <span className="text-white/80">Download</span> or <span className="text-white/80">Open in new tab</span> above to view it.
      </div>
    </div>
  );
}

function VideoStage({ att }: { att: Attachment }) {
  const url = useBlobUrl(att.id);
  if (!url) return <Spinner className="h-6 w-6 text-white/70" />;
  return <video src={url} controls autoPlay onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full rounded-lg" />;
}

/** Preview an HTML attachment (e.g. a self-contained demo) in a SANDBOXED iframe. The blob URL is
 *  same-origin, so we deliberately omit `allow-same-origin` — the document runs in an opaque origin
 *  and can't touch the app's cookies/DOM — while `allow-scripts` still lets the demo run. */
function HtmlStage({ att }: { att: Attachment }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let obj: string | null = null;
    setUrl(null);
    api
      .fetchAttachmentBlob(att.id)
      .then((b) => {
        if (cancelled) return;
        // Force text/html so the iframe renders (not downloads) it, whatever the stored MIME was.
        const html = b.type.includes('html') ? b : new Blob([b], { type: 'text/html' });
        obj = URL.createObjectURL(html);
        setUrl(obj);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [att.id]);
  if (!url) return <Spinner className="h-6 w-6 text-white/70" />;
  return (
    <div className="flex h-full w-full items-center justify-center p-3 sm:p-6">
      <iframe
        src={url}
        title={att.filename}
        sandbox="allow-scripts allow-popups allow-forms allow-modals"
        className="h-full w-full max-w-[1200px] rounded-lg border border-white/10 bg-white shadow-2xl"
      />
    </div>
  );
}

/** Zoomable/pannable image: wheel + pinch to zoom (toward the pointer), drag to pan, buttons + a
 *  double-click toggle. Zoom range 1×–8×. */
function ImageStage({ att }: { att: Attachment }) {
  const url = useBlobUrl(att.id);
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const pinch = useRef<{ dist: number; scale: number } | null>(null);

  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [url]);

  const clamp = (s: number) => Math.min(8, Math.max(1, s));
  const zoomAt = (factor: number, cx: number, cy: number) => {
    setScale((prev) => {
      const next = clamp(prev * factor);
      const ratio = next / prev;
      setTx((t) => cx - (cx - t) * ratio);
      setTy((t) => cy - (cy - t) * ratio);
      return next;
    });
  };
  const centerCoords = (clientX: number, clientY: number) => {
    const r = stageRef.current!.getBoundingClientRect();
    return { cx: clientX - r.left - r.width / 2, cy: clientY - r.top - r.height / 2 };
  };
  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const { cx, cy } = centerCoords(e.clientX, e.clientY);
    zoomAt(e.deltaY < 0 ? 1.18 : 1 / 1.18, cx, cy);
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (scale <= 1) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setTx(drag.current.tx + (e.clientX - drag.current.x));
    setTy(drag.current.ty + (e.clientY - drag.current.y));
  };
  const onPointerUp = () => {
    drag.current = null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    e.preventDefault();
    const [a, b] = [e.touches[0], e.touches[1]];
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (!pinch.current) {
      pinch.current = { dist, scale };
      return;
    }
    const { cx, cy } = centerCoords((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
    const target = clamp((pinch.current.scale * dist) / pinch.current.dist);
    zoomAt(target / scale, cx, cy);
  };

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <div
        ref={stageRef}
        className="flex h-full w-full items-center justify-center overflow-hidden"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onTouchMove={onTouchMove}
        onTouchEnd={() => (pinch.current = null)}
        onDoubleClick={(e) => {
          if (scale > 1) reset();
          else {
            const { cx, cy } = centerCoords(e.clientX, e.clientY);
            zoomAt(2.5, cx, cy);
          }
        }}
        style={{ cursor: scale > 1 ? 'grab' : 'zoom-in', touchAction: 'none' }}
      >
        {!url ? (
          <Spinner className="h-6 w-6 text-white/70" />
        ) : (
          <img
            src={url}
            alt={att.filename}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full select-none rounded-lg"
            style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transition: drag.current || pinch.current ? 'none' : 'transform 0.08s ease-out' }}
          />
        )}
      </div>
      {/* zoom controls */}
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-white/10 px-1.5 py-1 backdrop-blur" onClick={(e) => e.stopPropagation()}>
        <ZoomBtn label="Zoom out" onClick={() => zoomAt(1 / 1.4, 0, 0)}>−</ZoomBtn>
        <button type="button" onClick={reset} className="min-w-[46px] rounded-full px-2 py-1 text-center text-[12px] font-semibold text-white/90 tnum transition-colors hover:bg-white/10">
          {Math.round(scale * 100)}%
        </button>
        <ZoomBtn label="Zoom in" onClick={() => zoomAt(1.4, 0, 0)}>+</ZoomBtn>
      </div>
    </div>
  );
}

function ZoomBtn({ children, onClick, label }: { children: ReactNode; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="flex h-8 w-8 items-center justify-center rounded-full text-[18px] font-semibold text-white/90 transition-colors hover:bg-white/15">
      {children}
    </button>
  );
}
